import React, { useState, useMemo, useEffect } from 'react';
import { CycleCalendar, CycleLogForm, CyclePredictionCard, PeriodReminderCard, AICycleInsights, CycleHistory } from '../components/CycleTracker';
import { useCycleData } from '../hooks/useCycleData';
import { CycleEntry, CycleLogInput } from '../types';
import { useAuth } from '../contexts/AuthContext';
import IrregularityAlert from '../components/CycleTracker/IrregularityAlert';
import MissedPeriodAlert from '../components/CycleTracker/MissedPeriodAlert';

const CycleTracker: React.FC = () => {
  const { user } = useAuth();
  const { cycles, predictions, loading, error, logCycle, updateCycle, refetch } = useCycleData(user?.sub || 'demo-user');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!loading) {
      setTimeout(() => setIsVisible(true), 100);
    }
  }, [loading]);

  // Find if selected date has an existing cycle entry
  const existingEntry = useMemo((): CycleEntry | undefined => {
    if (!selectedDate) return undefined;
    
    return cycles.find(cycle => {
      const startDate = new Date(cycle.startDate);
      const endDate = cycle.endDate ? new Date(cycle.endDate) : new Date(startDate.getTime() + 5 * 24 * 60 * 60 * 1000);
      
      return selectedDate >= startDate && selectedDate <= endDate;
    });
  }, [selectedDate, cycles]);

  // Calculate current cycle day
  const currentCycleDay = useMemo((): number | null => {
    if (cycles.length === 0) return null;
    
    const sortedCycles = [...cycles].sort(
      (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
    
    const lastCycle = sortedCycles[0];
    const lastPeriodStart = new Date(lastCycle.startDate);
    const today = new Date();
    
    const diffTime = today.getTime() - lastPeriodStart.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    return diffDays > 0 ? diffDays : null;
  }, [cycles]);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setShowForm(true);
  };

  const handleFormSubmit = async (entry: CycleLogInput) => {
    if (existingEntry) {
      await updateCycle(existingEntry.startDate, entry);
    } else {
      await logCycle(entry);
    }
    setShowForm(false);
    setSelectedDate(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedDate(null);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-200 border-t-purple-600 mx-auto mb-4"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl animate-pulse">🌸</span>
              </div>
            </div>
            <p className="text-gray-600 animate-pulse">Loading your cycle data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundImage: 'url("/images/home-bg.jpg")', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', minHeight: '100vh' }}>
    <div className="bg-gradient-to-b from-white/30 via-white/10 to-white/40 min-h-screen">
    <div className={`max-w-7xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      {/* Header with fade-in animation */}
      <div className={`mb-8 transition-all duration-500 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        <h1 className="text-3xl font-bold text-gray-800 mb-2 bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">
          Cycle Tracker
        </h1>
        <p className="text-gray-600">
          Track your menstrual cycle, view predictions, and understand your body better.
        </p>
      </div>

      {/* Error Alert with shake animation */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between animate-shake">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-700">{error}</p>
          </div>
          <button
            onClick={refetch}
            className="text-red-600 hover:text-red-800 text-sm font-medium hover:scale-105 transition-transform"
          >
            Retry
          </button>
        </div>
      )}

      {/* Irregularity Alerts - Show if any recent cycles are irregular */}
      {cycles.some(c => c.isIrregular) && (
        <div className="mb-6">
          {cycles
            .filter(c => c.isIrregular)
            .slice(0, 1) // Show only the most recent irregular cycle
            .map(cycle => (
              <IrregularityAlert
                key={cycle.startDate}
                irregularity={{
                  isIrregular: cycle.isIrregular || false,
                  irregularityType: cycle.irregularityType || null,
                  severity: cycle.irregularitySeverity || null,
                  deviationDays: cycle.deviationDays || null,
                  consecutiveCount: cycles.filter(c => c.isIrregular).length,
                  recommendMedicalConsultation: cycle.irregularitySeverity === 'high' || 
                    cycles.filter(c => c.isIrregular).length >= 3,
                }}
                cycleDate={cycle.startDate}
              />
            ))}
        </div>
      )}

      {/* Missed Period Detection - Analyzes gaps between logged cycles */}
      {cycles.length >= 2 && (
        <div className="mb-6">
          <MissedPeriodAlert cycles={cycles} />
        </div>
      )}

      {/* Empty State - Show when no cycles logged */}
      {cycles.length === 0 && !showForm && (
        <div className={`mb-8 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-8 text-center transition-all duration-500 delay-200 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="text-6xl mb-4 animate-bounce-slow">🌸</div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Start Tracking Your Cycle</h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Log your first period to begin tracking. The more cycles you log, the more accurate your predictions will become.
          </p>
          <button
            onClick={() => {
              setSelectedDate(new Date());
              setShowForm(true);
            }}
            className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all duration-300 inline-flex items-center gap-2 shadow-md hover:shadow-lg hover:scale-105 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Log Your First Period
          </button>
        </div>
      )}

      {/* Main Content Grid */}
      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 transition-all duration-500 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Calendar - Takes 2 columns on large screens */}
        <div className="lg:col-span-2 transform transition-all duration-300 hover:scale-[1.01]">
          <CycleCalendar
            cycles={cycles}
            predictions={predictions}
            onDateSelect={handleDateSelect}
            selectedDate={selectedDate}
          />
        </div>

        {/* Sidebar - Predictions and Form */}
        <div className="space-y-6">
          {/* Show form when date is selected, otherwise show predictions */}
          <div className={`transition-all duration-300 ${showForm ? 'animate-slideIn' : ''}`}>
            {showForm ? (
              <CycleLogForm
                onSubmit={handleFormSubmit}
                initialDate={selectedDate || undefined}
                existingEntry={existingEntry}
                onCancel={handleFormCancel}
              />
            ) : (
              <>
                <PeriodReminderCard prediction={predictions} />
                <CyclePredictionCard
                  prediction={predictions}
                  currentCycleDay={currentCycleDay}
                />
                
                {/* Quick Log Button */}
                <button
                  onClick={() => {
                    setSelectedDate(new Date());
                    setShowForm(true);
                  }}
                  className="w-full py-3 px-4 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] group"
                >
                  <svg className="w-5 h-5 transition-transform group-hover:rotate-90 duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Log Period
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* AI Cycle Insights */}
      <div className={`mt-8 transition-all duration-500 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <AICycleInsights cycles={cycles} />
      </div>

      {/* Cycle History */}
      {cycles.length > 0 && (
        <div className={`mt-8 transition-all duration-500 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <CycleHistory
            cycles={cycles}
            onEditCycle={(cycle) => {
              setSelectedDate(new Date(cycle.startDate));
              setShowForm(true);
            }}
          />
        </div>
      )}

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
    </div>
    </div>
  );
};

export default CycleTracker;
